import { createFileRoute } from "@tanstack/react-router";
import { EsHiatalHerniaPage } from "@/components/site/HiatalHerniaPage";
import {
  ES_HIATAL_FIGURES,
  ES_HIATAL_PAGE,
  ES_HIATAL_PATH,
  ES_HIATAL_SLEEVE_SCAN,
  ES_HIATAL_SLEEVE_SCAN_SECOND,
  ES_HIATAL_TYPE1_REPAIR,
  ES_HIATAL_TYPE1_ESOPHAGUS,
  ES_HIATAL_TYPE1_COMPLETED,
  ES_HIATAL_TYPE1_WEIGHT,
  ES_HIATAL_TYPE2_SLEEVE_SCAN,
  ES_HIATAL_TYPE2_PATIENT_STORY,
  ES_HIATAL_TYPE3_SLEEVE_SCAN,
  ES_HIATAL_TYPE3_SLEEVE_SCAN_SECOND,
  ES_HIATAL_TYPE3_PATIENT_STORY,
  ES_HIATAL_TYPE4_PATIENT_STORY,
  ES_HIATAL_TYPE4_UPPER_GI,
  ES_HIATAL_RECURRENT_SCAR_TISSUE,
  ES_HIATAL_RECURRENT_COMPLETED_REPAIR,
  ES_HIATAL_RECURRENT_REDO_2,
  ES_HIATAL_RECURRENT_REDO_2_COMPLETE,
} from "@/lib/es-hiatal-hernia";
import { hiatalImageObjects } from "@/lib/hiatal-hernia";
import { procedureImageObjects } from "@/lib/procedure-media";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ORG_REF, PHYSICIAN_REF, SETTING_OUTPATIENT_ES } from "@/lib/medical-schema";

export const Route = createFileRoute("/es/reparacion-de-hernia-hiatal")({
  head: () => {
    const base = esHead("hiatalHernia");
    const url = `${SITE}${ES_HIATAL_PATH}`;
    const images = [
       ...hiatalImageObjects(ES_HIATAL_FIGURES, "es-US", url, [ES_HIATAL_SLEEVE_SCAN_SECOND, ES_HIATAL_TYPE2_SLEEVE_SCAN, ...ES_HIATAL_TYPE2_PATIENT_STORY, ES_HIATAL_TYPE3_SLEEVE_SCAN, ES_HIATAL_TYPE3_SLEEVE_SCAN_SECOND, ...ES_HIATAL_TYPE3_PATIENT_STORY, ES_HIATAL_SLEEVE_SCAN, ES_HIATAL_TYPE1_REPAIR, ES_HIATAL_TYPE1_ESOPHAGUS, ES_HIATAL_TYPE1_COMPLETED, ES_HIATAL_TYPE1_WEIGHT, ES_HIATAL_TYPE4_UPPER_GI, ...ES_HIATAL_TYPE4_PATIENT_STORY, ES_HIATAL_RECURRENT_SCAR_TISSUE, ES_HIATAL_RECURRENT_COMPLETED_REPAIR, ES_HIATAL_RECURRENT_REDO_2, ES_HIATAL_RECURRENT_REDO_2_COMPLETE]),
      ...procedureImageObjects(["sleeveUgiRefluxPreEs", "sleeveUgiRefluxPostEs"], "es", url),
    ];
    const scanUrl = `${SITE}${ES_HIATAL_SLEEVE_SCAN.full}`;
    return {
      ...base,
      meta: [
        ...base.meta.filter((entry) =>
          !(
            "property" in entry && typeof entry.property === "string" && ["og:image", "og:image:alt", "og:image:type", "og:image:width", "og:image:height"].includes(entry.property)
          ) && !(
            "name" in entry && typeof entry.name === "string" && ["twitter:image", "twitter:image:alt"].includes(entry.name)
          ),
        ),
        { property: "og:image", content: scanUrl },
        { property: "og:image:alt", content: ES_HIATAL_SLEEVE_SCAN.alt },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: String(ES_HIATAL_SLEEVE_SCAN.width) },
        { property: "og:image:height", content: String(ES_HIATAL_SLEEVE_SCAN.height) },
        { name: "twitter:image", content: scanUrl },
        { name: "twitter:image:alt", content: ES_HIATAL_SLEEVE_SCAN.alt },
      ],
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    ...esWebPageSchema("hiatalHernia"),
                    "@type": "MedicalWebPage",
                    reviewedBy: PHYSICIAN_REF,
                    mainEntity: { "@id": `${url}#procedimiento` },
                    about: [{ "@id": `${url}#procedimiento` }, {
                      "@type": "MedicalCondition",
                      name: "Hernia hiatal",
                      code: { "@type": "MedicalCode", codingSystem: "ICD-10", codeValue: "K44.9" },
                    }],
                    specialty: { "@type": "MedicalSpecialty", name: "Cirugía bariátrica" },
                    primaryImageOfPage: { "@id": `${url}#imagen-${images.length}` },
                  },
                  ...images.map((image, index) => ({
                    ...image,
                    "@id": `${url}#imagen-${index + 1}`,
                  })),
                  {
                    "@type": "MedicalProcedure",
                    "@id": `${url}#procedimiento`,
                    mainEntityOfPage: { "@id": `${url}#webpage` },
                    name: ES_HIATAL_PAGE.procedureName,
                    alternateName: ES_HIATAL_PAGE.procedureAlternateNames,
                    description: ES_HIATAL_PAGE.procedureDescription,
                    url,
                    image: images,
                    bodyLocation: "Hiato esofágico y estómago",
                    howPerformed: "Se realiza por laparoscopía mediante incisiones pequeñas: se reduce la hernia, se extrae el saco herniario, se moviliza el esófago y se cierran los pilares con suturas. Cuando corresponde, se añade una funduplicatura." + " " + SETTING_OUTPATIENT_ES + " Cuando se combina con una cirugía de manga o de bypass, la reparación forma parte de la noche de hospitalización de esa operación en INOVA Fair Oaks Hospital.",
                    preparation: "Estudio gastrointestinal superior con contraste y, cuando es necesario, endoscopia superior y prueba de pH, además de la evaluación médica preoperatoria habitual.",
                    followup: "Dieta posoperatoria por etapas, desde líquidos hasta alimentos sólidos durante varias semanas; no levantar objetos pesados durante cuatro semanas y seguimiento a largo plazo.",
                    status: "https://schema.org/ActiveActionStatus",
                    medicineSystem: "https://schema.org/WesternConventional",
                    relevantSpecialty: { "@type": "MedicalSpecialty", name: "Cirugía bariátrica" },
                    recognizingAuthority: ORG_REF,
                  },
                  {
                    "@type": "FAQPage",
                    "@id": `${url}#faq`,
                    inLanguage: "es-US",
                    mainEntity: ES_HIATAL_PAGE.faqs.map((faq) => ({
                      "@type": "Question",
                      name: faq.question,
                      acceptedAnswer: { "@type": "Answer", text: faq.answer },
                    })),
                  },
                  {
                    "@type": "BreadcrumbList",
                    "@id": `${url}#breadcrumb`,
                    itemListElement: [
                      { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE}/es` },
                      { "@type": "ListItem", position: 2, name: ES_HIATAL_PAGE.h1, item: url },
                    ],
                  },
                ],
              }),
            },
          ]
        : [],
    };
  },
  component: EsHiatalHerniaPage,
});
